import { AccountFeatureFlag, hasFeatureFlag } from '@/services/auth/feature-flags';
import { isBuilderPlan as isBuilderPlanAccount } from '@/services/auth/plan-slugs';
import { PlanFeature, type AugmentedAccount, type AugmentedUser } from '@/services/auth/types';
import type { TargetTransitionOptions } from '@/services/router/safe-navigate';
import { isEmptyString } from '@/utils/string';
import type { usePermissions } from '@customerio/hydra-permissions/react';
import type { IconName } from '@customerio/pluma-icons';
import { iconNames } from '@customerio/pluma-icons';
import type { MergedWorkspace, WorkspaceObjectType } from '../../../../features/workspaces';
import { AppBundle, AppBundleRoutes } from '../../../../utils/app-bundles';

export interface NavItemGroup {
  title?: string;
  items: NavItem[];
}

export interface NavItem {
  activateOnPipelinesRoutes?: string[];
  activeExceptUrls?: string[];
  activeWhenUrls?: string[];
  ariaLabel?: string;
  children?: NavItem[];
  href?: string;
  iconName?: IconName;
  id: string;
  isDropdownMenu?: boolean;
  isGreyedOut?: boolean;
  isDisabled?: boolean;
  isVisuallyDisabled?: boolean;
  disabledTooltip?: string;
  label: string;
  showNewLabel?: boolean;
  showPremiumLabel?: boolean;
  defaultIsOpen?: boolean;
  transitionOptions?: TargetTransitionOptions;
}

function shouldShowFullJourneysRoutesMenu(auth: { account: AugmentedAccount | null; user: AugmentedUser | null }) {
  const { user, account } = auth;

  if (user == null || account == null) {
    return false;
  }

  // if masqueraded into an account
  if (account?.is_masq ?? false) {
    // if the user doesn't have all permissions properly set to masq return false
    if (user.noMasqAccess || user.masqLevelNotSet) {
      return false;
    }

    // if the user has masq permissions and accessGrantedUntil is set return true
    if (user.isSuperUser || user.canReadWriteMasq || user.canReadOnlyMasq) {
      return true;
    }

    return false;
  }

  return true;
}

export function generateRoutes(
  currentWorkspace: MergedWorkspace,
  auth: { account: AugmentedAccount | null; user: AugmentedUser | null },
  { hasAnyPermission, hasPermission }: ReturnType<typeof usePermissions>,
  extraConfig?: {
    objectTypes?: WorkspaceObjectType[];
  },
): NavItemGroup[] {
  if (currentWorkspace.cio_workspace_id == null || currentWorkspace.cio_workspace_id === 0) {
    return [];
  }

  const { objectTypes } = extraConfig ?? {
    objectTypes: [],
  };
  // eslint-disable-next-line local-rules/enforce-generate-path -- prefix used to build many nav URLs
  const envPrefix = `/workspaces/${currentWorkspace.id}`;
  const envAppPrefix = `${envPrefix}${AppBundleRoutes[AppBundle.Journeys]}`;

  const showFullMenu = shouldShowFullJourneysRoutesMenu(auth);
  const isAnonymousInAppEnabled = auth.account?.planFeatures[PlanFeature.AnonymousInApp].enabled ?? false;

  const isGoalsTrackingEnabled = hasFeatureFlag(AccountFeatureFlag.GoalsTracking);
  const isAiEnabled = hasFeatureFlag(AccountFeatureFlag.AiEnabled);
  const isBuilderPlan = isBuilderPlanAccount(auth.account);
  const isAssetsInDsEnabled = hasFeatureFlag(AccountFeatureFlag.AssetsInDs);

  if (!showFullMenu) {
    return [];
  }

  return (
    [
      {
        items: [
          {
            id: 'dashboard',
            label: 'Home',
            href: `${envPrefix}/home`,
            iconName: 'home',
            activeWhenUrls: [`${envPrefix}/home`, `${envAppPrefix}/dashboard`, `${envAppPrefix}/home`],
          },
          isAiEnabled
            ? {
                id: 'agent',
                label: 'Agent',
                href: `${envPrefix}/agent`,
                iconName: 'sparkle',
              }
            : null,
        ],
      },
      isBuilderPlan
        ? {
            title: 'Review',
            items: [
              {
                id: 'logs',
                label: 'Activity Logs',
                href: `${envAppPrefix}/activity_logs`,
                iconName: 'activity',
              },
              {
                id: 'outbox',
                label: 'Deliveries',
                href: `${envAppPrefix}/outbox`,
                iconName: 'delivery-log',
              },
            ],
          }
        : {
            title: 'Review',
            items: [
              isGoalsTrackingEnabled
                ? {
                    id: 'goals',
                    label: 'Goals',
                    href: `${envAppPrefix}/goals`,
                    iconName: 'goal',
                  }
                : null,
              {
                id: 'analysis',
                label: 'Analysis',
                href: `${envAppPrefix}/analysis`,
                iconName: 'analysis',
              },
              {
                id: 'logs',
                label: 'Activity Logs',
                href: `${envAppPrefix}/activity_logs`,
                iconName: 'activity',
              },
              {
                id: 'outbox',
                label: 'Deliveries & Drafts',
                href: `${envAppPrefix}/outbox`,
                iconName: 'delivery-log',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Send messages',
            items: [
              {
                id: 'broadcasts',
                label: 'Broadcasts',
                href: `${envAppPrefix}/broadcasts`,
                iconName: 'broadcast',
              },
              {
                id: 'transactional',
                label: 'Transactional',
                href: `${envAppPrefix}/transactional`,
                iconName: 'transactional-api',
              },
            ],
          }
        : {
            title: 'Send messages',
            items: [
              {
                id: 'campaigns',
                label: 'Campaigns',
                href: `${envAppPrefix}/campaigns`,
                iconName: 'campaigns',
              },
              {
                id: 'broadcasts',
                label: 'Broadcasts',
                href: `${envAppPrefix}/broadcasts`,
                iconName: 'broadcast',
              },
              {
                id: 'transactional',
                label: 'Transactional',
                href: `${envAppPrefix}/transactional`,
                iconName: 'transactional-api',
              },
              isAnonymousInAppEnabled
                ? {
                    id: 'anonymous',
                    label: 'Anonymous',
                    href: `${envAppPrefix}/anonymous`,
                    iconName: 'anonymous',
                  }
                : null,
              {
                id: 'message-library',
                label: 'Message Library',
                href: `${envAppPrefix}/message-library`,
                iconName: 'folder',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Manage audience',
            items: [
              {
                id: 'people',
                label: 'People',
                href: `${envAppPrefix}/people`,
                iconName: 'person',
              },
              {
                id: 'segments',
                label: 'Segments',
                href: `${envAppPrefix}/segments`,
                iconName: 'segment',
              },
            ],
          }
        : {
            title: 'Manage audience',
            items: [
              {
                id: 'people',
                label: 'People',
                href: `${envAppPrefix}/people`,
                iconName: 'person',
              },
              ...generateObjectTypeRoutes(envAppPrefix, objectTypes ?? []),
              {
                id: 'segments',
                label: 'Segments',
                href: `${envAppPrefix}/segments`,
                iconName: 'segment',
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Create content',
            items: [
              {
                id: 'design-studio',
                label: 'Design Studio',
                iconName: 'design-studio-new',
                href: `${envPrefix}/design-studio/dashboard`,
              },
              {
                id: 'assets',
                label: 'Assets',
                href: isAssetsInDsEnabled ? `${envPrefix}/design-studio/assets` : `${envAppPrefix}/assets`,
                iconName: 'image',
                activeWhenUrls: [`${envAppPrefix}/assets`, `${envPrefix}/design-studio/assets`],
              },
              {
                id: 'snippets',
                label: 'Snippets',
                href: `${envAppPrefix}/snippets`,
                iconName: 'liquid',
              },
            ],
          }
        : {
            title: 'Create content',
            items: [
              {
                id: 'design-studio',
                label: 'Design Studio',
                iconName: 'design-studio-new',
                href: `${envPrefix}/design-studio/dashboard`,
              },
              {
                id: 'assets',
                label: 'Assets',
                href: isAssetsInDsEnabled ? `${envPrefix}/design-studio/assets` : `${envAppPrefix}/assets`,
                iconName: 'image',
                activeWhenUrls: [`${envAppPrefix}/assets`, `${envPrefix}/design-studio/assets`],
              },
              {
                id: 'create-content-more',
                ariaLabel: 'More create content options',
                label: 'More',
                iconName: 'more',
                isDropdownMenu: true,
                children: [
                  {
                    id: 'layouts',
                    label: 'Email Layouts',
                    href: `${envAppPrefix}/layouts`,
                    iconName: 'layout',
                  },
                  {
                    id: 'snippets',
                    label: 'Snippets',
                    href: `${envAppPrefix}/snippets`,
                    iconName: 'liquid',
                  },
                ],
              },
            ],
          },
      isBuilderPlan
        ? {
            title: 'Configure data',
            items: [
              {
                id: 'data-index',
                label: 'Data Index',
                href: `${envAppPrefix}/data-index`,
                iconName: 'data-index',
              },
              hasPermission('journeys/imports/view') === true
                ? {
                    id: 'imports',
                    label: 'Imports',
                    href: `${envAppPrefix}/imports`,
                    iconName: 'import' as IconName,
                  }
                : null,
              hasPermission('journeys/exports/view') === true
                ? {
                    id: 'exports',
                    label: 'Exports',
                    href: `${envAppPrefix}/exports`,
                    iconName: 'export' as IconName,
                  }
                : null,
            ],
          }
        : {
            title: 'Configure data',
            items: [
              {
                id: 'data-index',
                label: 'Data Index',
                href: `${envAppPrefix}/data-index`,
                iconName: 'data-index',
              },
              hasAnyPermission('journeys/imports/view', 'journeys/exports/view') === true
                ? {
                    id: 'integrations',
                    label: 'Integrations',
                    href: `${envAppPrefix}/integrations`,
                    activateOnPipelinesRoutes: ['/pipelines'],
                    iconName: 'integrations',
                  }
                : null,
              {
                id: 'configure-data-more',
                ariaLabel: 'More configure data options',
                label: 'More',
                iconName: 'more' as IconName,
                isDropdownMenu: true,
                children: [
                  hasPermission('journeys/imports/view') === true
                    ? {
                        id: 'imports',
                        label: 'Imports',
                        href: `${envAppPrefix}/imports`,
                        iconName: 'import' as IconName,
                      }
                    : null,
                  hasPermission('journeys/exports/view') === true
                    ? {
                        id: 'exports',
                        label: 'Exports',
                        href: `${envAppPrefix}/exports`,
                        iconName: 'export' as IconName,
                      }
                    : null,
                  {
                    id: 'collections',
                    label: 'Collections',
                    href: `${envAppPrefix}/collections`,
                    showPremiumLabel: auth.account?.planFeatures[PlanFeature.Collections].showPremiumLabel ?? true,
                    iconName: 'database' as IconName,
                  },
                ],
              },
            ],
          },
      auth.account?.id === 1 && !isBuilderPlan
        ? {
            items: [
              {
                id: 'internal-tools',
                label: 'Internal Tools',
                href: '/internal-tools',
                iconName: 'a-b-test',
              },
            ],
          }
        : { items: [] },
    ] as NavItemGroup[]
  )
    .map((group) => {
      const updatedGroup: NavItemGroup = { title: group.title, items: [] };

      for (const item of group.items ?? []) {
        if (item == null || item.isGreyedOut === true) {
          continue;
        }

        if ('children' in item && item.children != null) {
          const children = item.children.filter((child) => child != null && child.isGreyedOut !== true) as NavItem[];
          updatedGroup.items.push({ ...item, children } as NavItem);
        } else if (item != null) {
          updatedGroup.items.push(item as NavItem);
        }
      }

      return updatedGroup;
    })
    .filter((group) => group.items.length !== 0);
}

export function isNavItemActive(item: NavItem, currentUrl: string) {
  let isActive = false;

  if (item.activeWhenUrls != null) {
    isActive = item.activeWhenUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive && item.href != null) {
    isActive = currentUrl.startsWith(item.href);
  }

  if (isActive && item.activeExceptUrls != null) {
    isActive = !item.activeExceptUrls.some((url) => currentUrl.startsWith(url));
  }

  if (!isActive) {
    const isPipelinesRoute = currentUrl.includes('/pipelines');
    if (isPipelinesRoute && item.activateOnPipelinesRoutes != null && item.activateOnPipelinesRoutes.length > 0) {
      isActive = item.activateOnPipelinesRoutes.some((route) => route != null && currentUrl.includes(route));
    }
  }

  return isActive;
}

function generateObjectTypeRoutes(
  envAppPrefix: string,
  workspaceObjectTypes?: WorkspaceObjectType[],
  isGreyedOut = false,
) {
  const objectTypes =
    workspaceObjectTypes?.sort((a, b) => {
      if (a.name < b.name) {
        return -1;
      }

      if (a.name > b.name) {
        return 1;
      }

      return 0;
    }) ?? [];

  if (objectTypes.length === 0) {
    return [
      {
        id: 'objects',
        label: 'Custom Objects',
        href: `${envAppPrefix}/objects`,
        iconName: 'object',
        isGreyedOut,
      },
    ];
  }

  if (objectTypes.length <= 3) {
    return objectTypes.map((objectType) => {
      // Type guard: check if icon is a valid IconName
      const isValidIcon = (icon: string | undefined): icon is IconName => {
        return icon != null && !isEmptyString(icon.trim()) && (iconNames as readonly string[]).includes(icon);
      };

      const icon: IconName = isValidIcon(objectType.icon) ? objectType.icon : 'object';

      return {
        id: `object-types_${objectType.id}`,
        label: objectType.name,
        href: `${envAppPrefix}/objects/${objectType.slug}`,
        iconName: icon,
        isGreyedOut,
      };
    });
  }

  return [
    {
      id: 'custom-objects',
      label: 'Custom Objects',
      iconName: 'object',
      isGreyedOut,
      children: [
        ...objectTypes.map((objectType) => ({
          id: `object-types_${objectType.id}`,
          label: objectType.name,
          href: `${envAppPrefix}/objects/${objectType.slug}`,
        })),
      ],
    },
  ];
}
